<script setup>
/**
 * The achievements: every one there is, earned or not. Earned ones say when;
 * the rest say how far along they are. What each takes is
 * `lib/achievements.js`; what has been earned, `stores/achievements.js`.
 */
import { computed } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Meter from '../ui/Meter.vue'
import { closeSheet, state } from '../../stores/state'
import { t } from '../../stores/i18n'
import { achievements, achievementsReady, earnedCount } from '../../stores/achievements'
import { fullDay } from '../../lib/dates'

const since = computed(() => (state.settings.startedOn ? fullDay(state.settings.startedOn) : ''))

/** Earned first, most recent at the top; the rest in the order they are defined. */
const list = computed(() => {
  const earned = achievements.value.filter((a) => a.earned).sort((a, b) => b.earnedOn.localeCompare(a.earnedOn))
  return [...earned, ...achievements.value.filter((a) => !a.earned)]
})
</script>

<template>
  <Sheet
    large
    :title="t('achievements.title')"
    :subtitle="t('achievements.subtitle', { earned: earnedCount, total: achievements.length })"
    @close="closeSheet"
  >
    <p v-if="since" class="mb-4 text-[13px] text-muted" data-since>{{ t('achievements.since', { date: since }) }}</p>
    <p v-if="!achievementsReady" class="text-[13px] text-faint">{{ t('achievements.counting') }}</p>
    <ul v-else class="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3" data-achievements>
      <li
        v-for="a in list"
        :key="a.id"
        class="flex gap-3 rounded-xl border p-3"
        :class="a.earned ? 'border-accent/35 bg-accent-soft/40' : 'border-line-soft'"
        :data-achievement="a.id"
        :data-earned="a.earned"
      >
        <span
          class="grid size-10 shrink-0 place-items-center rounded-full"
          :class="a.earned ? 'bg-accent text-bg' : 'bg-raised text-faint'"
        >
          <Icon :name="a.earned ? 'trophy' : a.icon" :size="19" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="text-[14px] font-semibold" :class="a.earned ? '' : 'text-muted'">
            {{ t(`achievement.${a.id}.title`) }}
          </p>
          <p class="mt-0.5 text-[12.5px] leading-snug text-muted">{{ t(`achievement.${a.id}.desc`) }}</p>
          <p v-if="a.earned" class="mt-1.5 text-[12px] font-medium text-accent">
            {{ t('achievements.earnedOn', { date: fullDay(a.earnedOn) }) }}
          </p>
          <template v-else>
            <Meter
              :value="a.have / a.need"
              color="var(--c-faint)"
              :label="t(`achievements.unit.${a.unit}`, { have: a.have, count: a.need })"
            />
            <p class="tnum mt-1 text-[12px] text-faint">
              {{ t(`achievements.unit.${a.unit}`, { have: a.have, count: a.need }) }}
            </p>
          </template>
        </div>
      </li>
    </ul>
  </Sheet>
</template>
